import VCombobox, {
  createComboboxCollection,
  useCombobox,
} from '@voly/ui-components/src/v2/VCombobox';
import VFormField from '@voly/ui-components/src/v2/VFormField';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import VTag from '@voly/ui-components/src/v2/VTag';
import VTagsInput, { useTagsInput } from '@voly/ui-components/src/v2/VTagsInput';
import { KeyboardEvent, ReactElement, useId, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { TEditablePointField } from './editablePointField';

type TPointTagsProps = {
  tags: TEditablePointField<string[]> | undefined;
  options: string[];
  lockedTags?: string[];
  isTagLimitedShare: boolean;
};

type TEditablePointTagsProps = Omit<TPointTagsProps, 'tags'> & {
  tags: TEditablePointField<string[]>;
};

const TAG_SIZE = 'sm';
const TAG_VARIANT = 'subtle';
const COMPLETE_KEY = 'Tab';

const haveSameTags = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((tag) => right.includes(tag));

const matchesSearch = (tag: string, search: string): boolean =>
  tag.toLowerCase().includes(search.trim().toLowerCase());

const isWholeTagName = (tag: string, search: string): boolean =>
  tag.toLowerCase() === search.trim().toLowerCase();

const rejectTypedTag = (): boolean => false;

const findEmptyListMessageKey = (options: string[], unselectedTags: string[]): string => {
  if (options.length === 0) {
    return 'no_tags_created_for_site';
  }

  if (unselectedTags.length === 0) {
    return 'no_additional_tags_to_display';
  }

  return 'no_tags_found';
};

const TagChips = ({ tags }: { tags: string[] }): ReactElement[] =>
  tags.map((tag) => (
    <VTag key={tag} size={TAG_SIZE} variant={TAG_VARIANT}>
      {tag}
    </VTag>
  ));

const EditablePointTags = ({
  tags,
  options,
  lockedTags = [],
  isTagLimitedShare,
}: TEditablePointTagsProps): ReactElement => {
  const [draftTags, setDraftTags] = useState<string[] | null>(null);
  const [search, setSearch] = useState('');
  const [highlightedTag, setHighlightedTag] = useState<string | null>(null);
  const id = useId();
  const sharedInputIds = { input: `point-tags-input-${id}`, control: `point-tags-control-${id}` };

  const visibleTags = draftTags ?? tags.value;
  const shownTags = [...lockedTags, ...visibleTags];
  const unselectedTags = options.filter((option) => !shownTags.includes(option));
  const matchingTags = unselectedTags.filter((tag) => matchesSearch(tag, search));
  const typedTag = matchingTags.find((tag) => isWholeTagName(tag, search)) ?? null;
  const canRemoveTag = !(isTagLimitedShare && visibleTags.length === 1);
  const canClearTags = !isTagLimitedShare && visibleTags.length > 0;

  const isRemovable = (tag: string): boolean => canRemoveTag && !lockedTags.includes(tag);

  const resetSelection = (): void => {
    setDraftTags(null);
    setSearch('');
  };

  const pickTag = ({ value }: { value: string[] }): void => {
    const [pickedTag] = value;

    if (!pickedTag) {
      return;
    }

    setDraftTags([...visibleTags, pickedTag]);
  };

  const saveOnClose = ({ open }: { open: boolean }): void => {
    if (open) {
      return;
    }

    resetSelection();

    if (haveSameTags(visibleTags, tags.value)) {
      return;
    }

    tags.onSave(visibleTags);
  };

  const completeSearch = (event: KeyboardEvent<HTMLInputElement>): void => {
    const [firstTag] = matchingTags;

    if (event.key !== COMPLETE_KEY || event.shiftKey || !search.trim() || !firstTag) {
      return;
    }

    if (firstTag === typedTag) {
      return;
    }

    event.preventDefault();
    setSearch(firstTag);
  };

  const combobox = useCombobox({
    ids: sharedInputIds,
    collection: createComboboxCollection({ items: matchingTags }),
    value: [],
    openOnClick: true,
    closeOnSelect: false,
    selectionBehavior: 'clear',
    inputValue: search,
    onInputValueChange: ({ inputValue }): void => setSearch(inputValue),
    highlightedValue: highlightedTag ?? typedTag,
    onHighlightChange: ({ highlightedValue }): void => setHighlightedTag(highlightedValue),
    onValueChange: pickTag,
    onOpenChange: saveOnClose,
  });

  const keepTags = ({ value }: { value: string[] }): void => {
    const keptTags = visibleTags.filter((tag) => value.includes(tag));

    if (keptTags.length === visibleTags.length) {
      return;
    }

    if (isTagLimitedShare && keptTags.length === 0) {
      return;
    }

    if (combobox.open) {
      setDraftTags(keptTags);
      return;
    }

    resetSelection();
    tags.onSave(keptTags);
  };

  const tagsInput = useTagsInput({
    ids: sharedInputIds,
    value: shownTags,
    editable: false,
    validate: rejectTypedTag,
    onValueChange: keepTags,
  });

  return (
    <VFormField
      data-testid="point-tags"
      label={translate('tags')}
      helperText={undefined}
      errorText={undefined}
      invalid={false}
      readOnly={false}
    >
      <VCombobox.RootProvider value={combobox} lazyMount unmountOnExit>
        <VTagsInput.RootProvider value={tagsInput}>
          <VTagsInput.Control>
            {shownTags.map((tag, index) => (
              <VTagsInput.Item key={tag} index={index} value={tag}>
                <VTagsInput.ItemPreview>
                  <VTagsInput.ItemText>{tag}</VTagsInput.ItemText>
                  {isRemovable(tag) ? (
                    <VTagsInput.ItemDeleteTrigger aria-label={`Remove ${tag}`} />
                  ) : null}
                </VTagsInput.ItemPreview>
              </VTagsInput.Item>
            ))}
            <VCombobox.Input unstyled asChild>
              <VTagsInput.Input
                aria-label={translate('select_tags')}
                placeholder={visibleTags.length ? undefined : translate('select_tags')}
                onKeyDown={completeSearch}
              />
            </VCombobox.Input>
            {canClearTags ? <VTagsInput.ClearTrigger aria-label={translate('clear_all')} /> : null}
          </VTagsInput.Control>

          <VCombobox.Content>
            <VCombobox.Empty>
              {translate(findEmptyListMessageKey(options, unselectedTags))}
            </VCombobox.Empty>
            {matchingTags.map((tag) => (
              <VCombobox.Item key={tag} item={tag}>
                {tag}
              </VCombobox.Item>
            ))}
          </VCombobox.Content>
        </VTagsInput.RootProvider>
      </VCombobox.RootProvider>
    </VFormField>
  );
};

const PointTags = ({
  tags,
  options,
  lockedTags = [],
  isTagLimitedShare,
}: TPointTagsProps): ReactElement | null => {
  if (!tags) {
    return null;
  }

  if (tags.canEdit) {
    return (
      <EditablePointTags
        tags={tags}
        options={options}
        lockedTags={lockedTags}
        isTagLimitedShare={isTagLimitedShare}
      />
    );
  }

  return (
    <VFormField
      data-testid="point-tags"
      label={translate('tags')}
      helperText={undefined}
      errorText={undefined}
      invalid={false}
      readOnly
    >
      <VStackRow flexWrap="wrap">
        <TagChips tags={lockedTags} />

        <TagChips tags={tags.value} />
      </VStackRow>
    </VFormField>
  );
};

export default PointTags;
